/**
 * CARD VAULT — TCG binder (Digimon / Pokémon).
 * Search external card APIs, save favorites, beam any card to Telegram.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CardGame, CardResult, VaultCard } from "@black-wall/shared";

const BASE = "/api/cards";
const BTN =
  "px-3 py-2 text-xs font-mono uppercase tracking-wider border bg-transparent transition-colors disabled:opacity-40 cursor-pointer";

type AnyCard = CardResult | (VaultCard & { vaultId?: number });

/** Per-game envelope look. `art` is a real pack photo/render served from
 * apps/web/public/pack-art/<game>.jpg — omitted until that asset exists
 * (Gemini-generated, see cards-pack-opening docs). No `art` just means the
 * gradient-only look below, so this ships before the art does. */
const PACK_THEME: Record<CardGame, { bg: string; accent: string; label: string; art?: string }> = {
  yugioh: {
    bg: "linear-gradient(160deg, #1a0a2e 0%, #3d1466 50%, #1a0a2e 100%)",
    accent: "#c9a600",
    label: "DUEL MONSTERS",
    art: "/pack-art/yugioh.jpg",
  },
  pokemon: {
    bg: "linear-gradient(160deg, #1a0505 0%, #7a1414 50%, #1a0505 100%)",
    accent: "#ffcb05",
    label: "POKÉMON TCG",
    art: "/pack-art/pokemon.jpg",
  },
  digimon: {
    bg: "linear-gradient(160deg, #051a1a 0%, #0d4d4d 50%, #051a1a 100%)",
    accent: "#00c8c8",
    label: "DIGIMON",
  },
  unionarena: {
    bg: "linear-gradient(160deg, #14041c 0%, #4a0f66 50%, #14041c 100%)",
    accent: "#e6e6fa",
    label: "UNION ARENA",
  },
  cyberpunk: {
    bg: "linear-gradient(160deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)",
    accent: "#fcee0a",
    label: "CYBERPUNK TCG",
  },
};

/** Background layers for a closed-pack half: real art on top, theme
 * gradient underneath as fallback. A missing art file just 404s the top
 * layer — the gradient still renders — so this never depends on the art
 * existing yet, and upgrades automatically once a file lands at the path
 * named in PACK_THEME. */
function packHalfBg(theme: (typeof PACK_THEME)[CardGame], edge: "top" | "bottom") {
  if (!theme.art) return { backgroundImage: theme.bg };
  return {
    backgroundImage: `url(${theme.art}), ${theme.bg}`,
    backgroundSize: "200px 300px, auto",
    backgroundPosition: `${edge}, 0 0`,
    backgroundRepeat: "no-repeat, no-repeat",
  };
}

/** Closed booster pack — tap to tear it open. `onTear` fires once; the
 * caller unmounts this after the tear animation (500ms, see
 * .bw-pack-tearing in styles.css) finishes. */
function PackEnvelope({ game, tearing, onTear, setLabel }: { game: CardGame; tearing: boolean; onTear: () => void; setLabel?: string }) {
  const theme = PACK_THEME[game];
  return (
    <div className="flex justify-center py-6">
      <div
        className={`bw-pack${tearing ? " bw-pack-tearing" : ""}`}
        onClick={() => !tearing && onTear()}
        role="button"
        aria-label="Rasgar sobre"
      >
        <div
          className="bw-pack-half bw-pack-half-top"
          style={{ ...packHalfBg(theme, "top"), border: `1px solid ${theme.accent}` }}
        >
          <div className="bw-pack-foil" />
          {/* Real art already carries its own branding — the text label only
           * covers for the flat gradient placeholder. */}
          {!theme.art && (
            <span className="text-xs font-bold tracking-[0.2em]" style={{ color: theme.accent }}>
              {theme.label}
            </span>
          )}
        </div>
        <div
          className="bw-pack-half bw-pack-half-bottom"
          style={{ ...packHalfBg(theme, "bottom"), border: `1px solid ${theme.accent}` }}
        >
          <div className="bw-pack-foil" />
          {!tearing && (
            <span className="text-[10px] uppercase tracking-wider animate-pulse" style={{ color: theme.accent, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
              {setLabel ?? "Tocá para abrir"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Which games have a pack recipe implemented server-side (services/pack-opener.ts).
 * Digimon has no generic recipe — see docs/cards-pack-opening-research.md — flip
 * this once a game's ticket lands instead of hitting a 400 from the API. */
const PACK_GAMES: Record<CardGame, boolean> = {
  yugioh: true,
  pokemon: true,
  digimon: true,
  unionarena: true,
  cyberpunk: false,
};

/**
 * Holographic shine + tilt, driven by pointer position. Rainbow streak
 * follows the cursor (mix-blend-mode: color-dodge, classic holo trick);
 * mobile gets touch tracking too. Gated by the `holo` flag — real for
 * Pokémon (TCGdex variant data), heuristic-by-rarity for Digimon/Yu-Gi-Oh.
 */
function HoloImage({ src, alt, holo }: { src: string; alt: string; holo: boolean }) {
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const trackFrom = (clientX: number, clientY: number, rect: DOMRect) => {
    setPos({
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <div
      className="relative select-none"
      style={{ perspective: 800 }}
      onMouseMove={(e) => holo && trackFrom(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setPos({ x: 50, y: 50 })}
      onTouchMove={(e) => {
        if (!holo) return;
        const t = e.touches[0];
        if (t) trackFrom(t.clientX, t.clientY, e.currentTarget.getBoundingClientRect());
      }}
      onTouchEnd={() => setPos({ x: 50, y: 50 })}
    >
      <img
        src={src}
        alt={alt}
        className="max-h-[70vh] border"
        style={{
          borderColor: "var(--bw-accent)",
          transform: holo
            ? `rotateY(${(pos.x - 50) / 8}deg) rotateX(${-(pos.y - 50) / 8}deg) scale(1.02)`
            : undefined,
          transition: "transform 120ms ease-out",
        }}
      />
      {holo && (
        <div
          className="pointer-events-none absolute inset-0 border"
          style={{
            borderColor: "var(--bw-accent)",
            background: `linear-gradient(${pos.x * 3.6}deg, transparent 30%, rgba(255,255,255,0.55) 45%, rgba(0,240,255,0.35) 50%, rgba(255,0,200,0.35) 55%, transparent 70%)`,
            backgroundPosition: `${pos.x}% ${pos.y}%`,
            backgroundSize: "220% 220%",
            mixBlendMode: "color-dodge",
            opacity: 0.65,
            transition: "opacity 120ms ease-out",
          }}
        />
      )}
    </div>
  );
}

export function CardsRoute() {
  const qc = useQueryClient();
  const [game, setGame] = useState<CardGame>("digimon");
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [searchSet, setSearchSet] = useState("");
  const [tab, setTab] = useState<"search" | "vault" | "pack" | "gallery">("vault");
  const [selected, setSelected] = useState<AnyCard | null>(null);
  const [galleryImg, setGalleryImg] = useState<string | null>(null);
  const [packGame, setPackGame] = useState<CardGame>("yugioh");
  const [packSet, setPackSet] = useState<string>("");
  const [packCards, setPackCards] = useState<CardResult[]>([]);
  const [revealed, setRevealed] = useState(0);
  // closed: unopened envelope shown, tap to tear; tearing: halves flying
  // apart (animation running); open: envelope gone, card grid reveals.
  const [packPhase, setPackPhase] = useState<"closed" | "tearing" | "open">("closed");

  const search = useQuery<{ cards: CardResult[]; error?: string }>({
    queryKey: ["cards", "search", game, q, searchSet],
    queryFn: async () =>
      (
        await fetch(
          `${BASE}/search?game=${game}&q=${encodeURIComponent(q)}${searchSet ? `&set=${encodeURIComponent(searchSet)}` : ""}`
        )
      ).json(),
    enabled: tab === "search" && (q.length > 1 || searchSet.length > 0),
  });

  const searchSets = useQuery<{ sets: Array<{ code: string; name: string }> }>({
    queryKey: ["cards", "sets", game],
    queryFn: async () => (await fetch(`${BASE}/sets?game=${game}`)).json(),
    enabled: tab === "search",
    staleTime: 24 * 60 * 60 * 1000,
  });

  const vault = useQuery<{ cards: VaultCard[] }>({
    queryKey: ["cards", "vault"],
    queryFn: async () => (await fetch(`${BASE}/vault`)).json(),
  });

  // Colecciones de arte — galería de ilustraciones limpias servidas como
  // estáticos desde apps/web/public/collections/. No son cartas (no tienen
  // card_number), así que viven fuera del vault: manifest estático, sin API.
  const collections = useQuery<{
    collections: Array<{ id: string; title: string; source: string; dir: string; images: string[] }>;
  }>({
    queryKey: ["cards", "collections"],
    queryFn: async () => (await fetch("/collections/index.json")).json(),
    enabled: tab === "gallery",
    staleTime: 24 * 60 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: async (card: CardResult) => {
      const res = await fetch(`${BASE}/vault`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(card),
      });
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cards", "vault"] }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) =>
      (await fetch(`${BASE}/vault/${id}`, { method: "DELETE" })).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cards", "vault"] });
      setSelected(null);
    },
  });

  const send = useMutation({
    mutationFn: async (card: AnyCard) => {
      const res = await fetch(`${BASE}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageUrl: card.imageUrl,
          caption: `${card.name} [${card.game}/${card.cardId}]`,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "send failed");
      return res.json();
    },
  });

  const packSets = useQuery<{ sets: Array<{ code: string; name: string }> }>({
    queryKey: ["cards", "sets", packGame],
    queryFn: async () => (await fetch(`${BASE}/sets?game=${packGame}`)).json(),
    enabled: tab === "pack" && PACK_GAMES[packGame],
    staleTime: 24 * 60 * 60 * 1000,
  });

  const openPack = useMutation({
    mutationFn: async ({ game, setCode }: { game: CardGame; setCode?: string }) => {
      const res = await fetch(`${BASE}/${game}/pack/open`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(setCode ? { setCode } : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "pack open failed");
      const cards = data.cards as CardResult[];
      // Preload every card image so the post-tear reveal is instant — the
      // tear animation should never wait on the network.
      await Promise.all(
        cards.map(
          (c) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = c.imageUrl;
              setTimeout(resolve, 8000);
            })
        )
      );
      return cards;
    },
    onSuccess: (cards) => {
      setPackCards(cards);
      setRevealed(0);
      setPackPhase("closed");
    },
  });

  // Prefetch (and preload) a pack as soon as the pack tab / game / set
  // selection settles — the envelope only renders once its cards are ready.
  useEffect(() => {
    if (tab !== "pack" || !PACK_GAMES[packGame]) return;
    setPackCards([]);
    setPackPhase("closed");
    openPack.mutate({ game: packGame, setCode: packSet || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, packGame, packSet]);

  // Tear animation runs for 500ms (matches .bw-pack-tearing in styles.css),
  // then the envelope unmounts and the reveal grid takes over.
  useEffect(() => {
    if (packPhase !== "tearing") return;
    const t = setTimeout(() => setPackPhase("open"), 520);
    return () => clearTimeout(t);
  }, [packPhase]);

  // Reveal the pack sequentially — commons first, the cascade/rare-or-better
  // slot last (already the order the API returns them in) — instead of
  // dumping all N cards at once, matching a real pack's build-up. Gated on
  // the envelope being torn open so cards don't appear before the pack does.
  useEffect(() => {
    if (packPhase !== "open" || revealed >= packCards.length) return;
    const t = setTimeout(() => setRevealed((n) => n + 1), 450);
    return () => clearTimeout(t);
  }, [packPhase, revealed, packCards.length]);

  const vaultKeys = new Set((vault.data?.cards ?? []).map((c) => `${c.game}:${c.cardId}`));
  const cards: AnyCard[] =
    tab === "vault" ? vault.data?.cards ?? [] : tab === "search" ? search.data?.cards ?? [] : [];

  const holo = useQuery<{ holo: boolean; rarity?: string }>({
    queryKey: ["cards", "holo", selected?.game, selected?.cardId],
    queryFn: async () => {
      const rarity = selected?.meta?.rarity ?? "";
      return (
        await fetch(
          `${BASE}/holo?game=${selected!.game}&cardId=${encodeURIComponent(selected!.cardId)}&rarity=${encodeURIComponent(rarity)}`
        )
      ).json();
    },
    enabled: !!selected,
  });

  return (
    <div className="p-4 font-mono" style={{ color: "var(--bw-fg)" }}>
      {/* Header + tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--bw-accent)" }}>
          Card Vault
        </span>
        {(["vault", "search", "pack", "gallery"] as const).map((t) => (
          <button
            key={t}
            className={BTN}
            style={{
              borderColor: tab === t ? "var(--bw-accent)" : "var(--bw-glass-border)",
              color: tab === t ? "var(--bw-accent)" : "var(--bw-muted)",
            }}
            onClick={() => setTab(t)}
          >
            {t === "vault"
              ? `Baúl (${vault.data?.cards.length ?? 0})`
              : t === "search"
                ? "Buscar"
                : t === "pack"
                  ? "Abrir sobre"
                  : "Colecciones"}
          </button>
        ))}
      </div>

      {/* Search controls */}
      {tab === "search" && (
        <form
          className="mb-4 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(input.trim());
          }}
        >
          {(["digimon", "pokemon", "yugioh", "unionarena", "cyberpunk"] as const).map((g) => (
            <button
              key={g}
              type="button"
              className={BTN}
              style={{
                borderColor: game === g ? "var(--bw-accent)" : "var(--bw-glass-border)",
                color: game === g ? "var(--bw-accent)" : "var(--bw-muted)",
              }}
              onClick={() => {
                setGame(g);
                setSearchSet("");
              }}
            >
              {g}
            </button>
          ))}
          <select
            value={searchSet}
            onChange={(e) => setSearchSet(e.target.value)}
            className="border px-2 py-2 text-xs font-mono uppercase tracking-wider"
            style={{ borderColor: "var(--bw-glass-border)", color: "var(--bw-fg)", background: "var(--bw-glass-bg)" }}
          >
            <option value="">Todos los sets</option>
            {(searchSets.data?.sets ?? []).map((st) => (
              <option key={st.code} value={st.code}>
                {st.name}
              </option>
            ))}
          </select>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="agumon, pikachu…"
            className="min-w-48 flex-1 border bg-transparent px-3 py-2 text-sm outline-none"
            style={{ borderColor: "var(--bw-glass-border)" }}
          />
          <button type="submit" className={BTN} style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }}>
            Buscar
          </button>
        </form>
      )}

      {/* Pack opener controls */}
      {tab === "pack" && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {(["digimon", "pokemon", "yugioh", "unionarena", "cyberpunk"] as const).map((g) => (
            <button
              key={g}
              type="button"
              disabled={!PACK_GAMES[g]}
              className={BTN}
              style={{
                borderColor: packGame === g ? "var(--bw-accent)" : "var(--bw-glass-border)",
                color: packGame === g ? "var(--bw-accent)" : "var(--bw-muted)",
              }}
              onClick={() => setPackGame(g)}
            >
              {g}
              {!PACK_GAMES[g] && " (próximamente)"}
            </button>
          ))}
          <select
            value={packSet}
            onChange={(e) => setPackSet(e.target.value)}
            className="border px-2 py-2 text-xs font-mono uppercase tracking-wider"
            style={{ borderColor: "var(--bw-glass-border)", color: "var(--bw-fg)", background: "var(--bw-glass-bg)" }}
          >
            <option value="">Todos los sets</option>
            {(packSets.data?.sets ?? []).map((st) => (
              <option key={st.code} value={st.code}>
                {st.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {tab === "pack" && openPack.isPending && (
        <div className="mb-3 text-xs animate-pulse" style={{ color: "var(--bw-muted)" }}>
          preparando sobre…
        </div>
      )}
      {tab === "pack" && openPack.isError && (
        <div className="mb-3 text-xs" style={{ color: "#ff5370" }}>
          {(openPack.error as Error).message}
        </div>
      )}
      {tab === "pack" && packCards.length === 0 && !openPack.isPending && (
        <div className="mb-3 text-xs" style={{ color: "var(--bw-muted)" }}>
          Elegí juego y expansión — el sobre se prepara solo y se abre al tocarlo.
        </div>
      )}
      {tab === "pack" && packCards.length > 0 && packPhase !== "open" && (
        <PackEnvelope game={packGame} tearing={packPhase === "tearing"} onTear={() => setPackPhase("tearing")} setLabel={packSets.data?.sets.find((st) => st.code === packSet)?.name} />
      )}
      {tab === "pack" && packCards.length > 0 && packPhase === "open" && (
        <div className="mb-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9">
          {packCards.slice(0, revealed).map((card, i) => {
            const isHit = i === packCards.length - 1; // last slot = the pack's biggest hit
            return (
              <button
                key={`${card.cardId}-${i}`}
                className="group relative cursor-pointer border p-1 transition-transform hover:scale-[1.05]"
                style={{
                  borderColor: isHit ? "var(--bw-accent)" : "var(--bw-glass-border)",
                  background: "var(--bw-glass-bg)",
                  animation: "bw-pack-reveal 300ms ease-out",
                }}
                onClick={() => setSelected(card)}
              >
                <img src={card.imageUrl} alt={card.name} loading="lazy" className="aspect-[0.72] w-full object-cover" />
                <div className="truncate px-1 py-1 text-[9px] uppercase tracking-wider" style={{ color: "var(--bw-muted)" }}>
                  {card.name}
                </div>
              </button>
            );
          })}
          {revealed < packCards.length && (
            <div
              className="flex aspect-[0.72] items-center justify-center border text-[10px] uppercase tracking-wider"
              style={{ borderColor: "var(--bw-glass-border)", color: "var(--bw-muted)" }}
            >
              ?
            </div>
          )}
          {revealed >= packCards.length && (
            <button
              type="button"
              className={`${BTN} aspect-[0.72]`}
              style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }}
              disabled={openPack.isPending}
              onClick={() => openPack.mutate({ game: packGame, setCode: packSet || undefined })}
            >
              {openPack.isPending ? "…" : "Abrir otro"}
            </button>
          )}
        </div>
      )}

      {/* Status line */}
      {tab === "search" && search.isFetching && (
        <div className="mb-3 text-xs" style={{ color: "var(--bw-muted)" }}>consultando la red…</div>
      )}
      {tab === "search" && q && !search.isFetching && cards.length === 0 && (
        <div className="mb-3 text-xs" style={{ color: "var(--bw-muted)" }}>sin resultados para «{q}»</div>
      )}
      {tab === "vault" && !vault.isLoading && cards.length === 0 && (
        <div className="text-xs" style={{ color: "var(--bw-muted)" }}>
          El baúl está vacío — busca cartas y guárdalas.
        </div>
      )}

      {/* Colecciones — galería de ilustraciones */}
      {tab === "gallery" && (
        <div>
          {collections.isLoading && (
            <div className="mb-3 text-xs animate-pulse" style={{ color: "var(--bw-muted)" }}>
              cargando colecciones…
            </div>
          )}
          {!collections.isLoading && (collections.data?.collections ?? []).length === 0 && (
            <div className="text-xs" style={{ color: "var(--bw-muted)" }}>
              Sin colecciones todavía.
            </div>
          )}
          {(collections.data?.collections ?? []).map((col) => (
            <section key={col.id} className="mb-6">
              <div className="mb-2 flex flex-wrap items-baseline gap-2">
                <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--bw-accent)" }}>
                  {col.title}
                </span>
                <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--bw-muted)" }}>
                  {col.images.length} ilustraciones · {col.source}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {col.images.map((img) => {
                  const url = `${col.dir}${img}`;
                  return (
                    <button
                      key={img}
                      className="group relative cursor-pointer border p-1 transition-transform hover:scale-[1.03]"
                      style={{ borderColor: "var(--bw-glass-border)", background: "var(--bw-glass-bg)" }}
                      onClick={() => setGalleryImg(url)}
                    >
                      <img src={url} alt={img} loading="lazy" className="aspect-[0.7] w-full object-cover" />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Card grid */}
      {tab !== "pack" && tab !== "gallery" && (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {cards.map((card) => {
          const saved = vaultKeys.has(`${card.game}:${card.cardId}`);
          return (
            <button
              key={`${card.game}:${card.cardId}`}
              className="group relative cursor-pointer border p-1 transition-transform hover:scale-[1.03]"
              style={{
                borderColor: saved ? "var(--bw-accent)" : "var(--bw-glass-border)",
                background: "var(--bw-glass-bg)",
              }}
              onClick={() => setSelected(card)}
            >
              <img
                src={card.imageUrl}
                alt={card.name}
                loading="lazy"
                className="aspect-[0.72] w-full object-cover"
              />
              <div className="truncate px-1 py-1 text-[10px] uppercase tracking-wider" style={{ color: "var(--bw-muted)" }}>
                {card.name}
              </div>
            </button>
          );
        })}
      </div>
      )}

      {/* Gallery lightbox — imagen limpia a pantalla completa */}
      {galleryImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setGalleryImg(null)}
        >
          <img
            src={galleryImg}
            alt=""
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="flex max-h-full flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <HoloImage src={selected.imageUrl} alt={selected.name} holo={Boolean(holo.data?.holo)} />
            <div className="text-sm">
              {selected.name}{" "}
              {holo.data?.holo && <span style={{ color: "var(--bw-accent)" }}>✨ HOLO</span>}{" "}
              <span style={{ color: "var(--bw-muted)" }}>
                [{selected.game}/{selected.cardId}]
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {"id" in selected ? (
                <button
                  className={BTN}
                  style={{ borderColor: "#ff5370", color: "#ff5370" }}
                  onClick={() => remove.mutate((selected as VaultCard).id)}
                  disabled={remove.isPending}
                >
                  Quitar del baúl
                </button>
              ) : (
                <button
                  className={BTN}
                  style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }}
                  onClick={() => save.mutate(selected as CardResult)}
                  disabled={save.isPending || vaultKeys.has(`${selected.game}:${selected.cardId}`)}
                >
                  {vaultKeys.has(`${selected.game}:${selected.cardId}`) ? "En el baúl ✓" : "Guardar en baúl"}
                </button>
              )}
              <button
                className={BTN}
                style={{ borderColor: "var(--bw-glass-border)", color: "var(--bw-fg)" }}
                onClick={() => send.mutate(selected)}
                disabled={send.isPending}
              >
                {send.isPending ? "Enviando…" : send.isSuccess ? "Enviada ✓" : "Enviar por Telegram"}
              </button>
              <button className={BTN} style={{ borderColor: "var(--bw-glass-border)", color: "var(--bw-muted)" }} onClick={() => setSelected(null)}>
                Cerrar
              </button>
            </div>
            {send.isError && (
              <div className="text-xs" style={{ color: "#ff5370" }}>
                {(send.error as Error).message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
